import { ArrowLeft, ArrowRight } from 'lucide-react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updateAdditionalInfo } from '../../redux/Slices/ResumeSlice';
import Button from '../Common/Button';

type Props = {
  step: number;
  totalSteps: number;
  stepName: string;
  prevStepName: string;
  onNext: () => void;
  onBack: () => void;
};

type AdditionalInfo = {
  socialLinks?: string;
  codingProfiles?: string;
  publications?: string;
  additionalDetails?: string;
};

const AdditionalInfoForm: React.FC<Props> = ({
  step,
  totalSteps,
  stepName,
  prevStepName,
  onNext,
  onBack
}) => {
  const dispatch = useDispatch();

  const additionalInfo: AdditionalInfo = useSelector(
    (state: any) => state.root.resume.additionalInfo
  );

  const [errors, setErrors] = React.useState({
    socialLinks: '',
    codingProfiles: '',
    publications: '',
    additionalDetails: ''
  });

  const PUBLICATIONS_LIMIT = 200;
  const ADDITIONAL_DETAILS_LIMIT = 100;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    validateField(name, value);

    dispatch(
      updateAdditionalInfo({
        [name]: value
      })
    );
  };

  /* changed to normal function */
  function CharCount(value: string, max: number) {
    const len = value?.length || 0;

    return (
      <span
        className={`text-xs ${len >= max ? 'text-red-500' : 'text-gray-400'}`}
      >
        {len}/{max}
      </span>
    );
  }

  const isFormValid = () => {
    return (
      !errors.socialLinks &&
      !errors.codingProfiles &&
      !errors.publications &&
      !errors.additionalDetails &&
      (additionalInfo.publications?.length || 0) <= PUBLICATIONS_LIMIT &&
      (additionalInfo.additionalDetails?.length || 0) <=
        ADDITIONAL_DETAILS_LIMIT
    );
  };

  const isValidUrl = (value: string) => {
    const urlPattern = /^(https?:\/\/)?([\w\d-]+\.)+[\w-]{2,}(\/.*)?$/i;

    return urlPattern.test(value);
  };

  const validateField = (name: string, value: string) => {
    let message = '';

    if (name === 'socialLinks' && value && !isValidUrl(value)) {
      message = 'Please provide valid URL';
    }

    if (name === 'codingProfiles' && value && !isValidUrl(value)) {
      message = 'Please provide valid URL';
    }

    if (name === 'publications' && value.length === PUBLICATIONS_LIMIT) {
      message = 'Maximum 200 characters reached';
    }

    if (
      name === 'additionalDetails' &&
      value.length === ADDITIONAL_DETAILS_LIMIT
    ) {
      message = 'Maximum 100 characters reached';
    }

    setErrors(prev => ({
      ...prev,
      [name]: message
    }));
  };

  return (
    <div className='w-full bg-gamma'>
      <div className='flex-1 p-10'>
        <div className='mb-4 flex justify-between'>
          <div>
            <p className='text-sm text-gray-500'>
              Step {step} of {totalSteps}
            </p>

            <h1 className='text-2xl font-semibold'>{stepName}</h1>

            <p className='mt-1 text-sm text-gray-400'>
              Complete your profile with additional details
            </p>
          </div>

          <div className='text-brand'>
            {Math.round(((step - 1) / totalSteps) * 100)} %
          </div>
        </div>

        <div className='mb-8 h-1 rounded bg-gray-200'>
          <div
            className='h-1 rounded bg-brand'
            style={{
              width: `${((step - 1) / totalSteps) * 100}%`
            }}
          />
        </div>

        <div className='rounded-xl bg-white p-6 shadow-sm'>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <label className='text-sm font-medium'>Social Links</label>

              <input
                name='socialLinks'
                value={additionalInfo.socialLinks || ''}
                onChange={handleChange}
                placeholder='LinkedIn / Portfolio'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
                ${errors.socialLinks ? 'border-red-500' : 'border-gray-300'}`}
              />

              {errors.socialLinks && (
                <p className='mt-1 text-sm text-red-500'>
                  {errors.socialLinks}
                </p>
              )}
            </div>

            <div>
              <label className='text-sm font-medium'>Coding Profiles</label>

              <input
                name='codingProfiles'
                value={additionalInfo.codingProfiles || ''}
                onChange={handleChange}
                placeholder='GitHub / LeetCode URL'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
                ${
                  errors.codingProfiles ? 'border-red-500' : 'border-gray-300'
                }`}
              />

              {errors.codingProfiles && (
                <p className='mt-1 text-sm text-red-500'>
                  {errors.codingProfiles}
                </p>
              )}
            </div>
          </div>

          <div className='mt-4'>
            <div className='flex items-center justify-between'>
              <label className='text-sm font-medium'>Publications</label>

              {CharCount(additionalInfo.publications || '', PUBLICATIONS_LIMIT)}
            </div>

            <textarea
              name='publications'
              value={additionalInfo.publications || ''}
              onChange={handleChange}
              maxLength={PUBLICATIONS_LIMIT}
              placeholder='Research papers, articles'
              rows={3}
              className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
              ${errors.publications ? 'border-red-500' : 'border-gray-300'}`}
            />

            {errors.publications && (
              <p className='mt-1 text-sm text-red-500'>{errors.publications}</p>
            )}
          </div>

          <div className='mt-4'>
            <div className='flex items-center justify-between'>
              <label className='text-sm font-medium'>Additional Details</label>

              {CharCount(
                additionalInfo.additionalDetails || '',
                ADDITIONAL_DETAILS_LIMIT
              )}
            </div>

            <textarea
              name='additionalDetails'
              value={additionalInfo.additionalDetails || ''}
              onChange={handleChange}
              maxLength={ADDITIONAL_DETAILS_LIMIT}
              placeholder='Anything else about you'
              rows={4}
              className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
              ${
                errors.additionalDetails ? 'border-red-500' : 'border-gray-300'
              }`}
            />

            {errors.additionalDetails && (
              <p className='mt-1 text-sm text-red-500'>
                {errors.additionalDetails}
              </p>
            )}
          </div>
        </div>

        <div className='mt-10 flex items-center justify-between'>
          <Button
            label={`Previous: ${prevStepName}`}
            handleClickBtn={onBack}
            variant='outlined'
          >
            <ArrowLeft size={16} />
          </Button>

          <Button
            label='Select Template'
            handleClickBtn={onNext}
            variant='contained'
            disabled={!isFormValid()}
            iconPosition='right'
          >
            <ArrowRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdditionalInfoForm;
